import { Component } from '@theme/component';

class KentCardColours extends Component {
  #abortController;
  #request = 0;
  #card;
  #quickAdd;
  #quickAddWasInert = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    ++this.#request;
    this.#abortController?.abort();
    this.#setPending(false);
    this.#initialize();
  }

  #initialize() {
    if (this.hasAttribute('data-links-only')) return;
    this.#card = this.closest('product-card, [data-kent-colour-card]');
    if (!this.#card) return;
    this.#quickAdd = this.#card.querySelector('quick-add-component');
    if (this.#quickAdd?.querySelector('.quick-add__button--choose')) {
      this.#quickAdd.dataset.quickAddButton = 'choose';
      this.#quickAdd.dataset.rendersBothButtons = 'false';
    }
    for (const choice of this.querySelectorAll('[data-colour-name][data-image-url]')) {
      if (!choice.dataset.variantId) continue;
      choice.setAttribute('role', 'button');
      if (!choice.hasAttribute('aria-pressed')) choice.setAttribute('aria-pressed', 'false');
    }
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    ++this.#request;
    this.#abortController?.abort();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.#setPending(false);
  }

  #onKeydown = (event) => {
    if (event.key !== ' ' || event.target?.getAttribute('role') !== 'button') return;
    event.preventDefault();
    event.target.click();
  };

  #onClick = (event) => {
    const choice = event.target.closest('[data-colour-name]');
    if (!choice || !this.contains(choice) || !choice.dataset.imageUrl || !choice.dataset.variantId) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.#select(choice);
  };

  #setPending(pending) {
    if (!this.#card) return;
    this.#card.toggleAttribute('data-kent-colour-pending', pending);
    this.setAttribute('aria-busy', String(pending));
    if (this.#quickAdd) {
      if (pending && !this.#quickAdd.hasAttribute('data-kent-pending')) {
        this.#quickAddWasInert = this.#quickAdd.inert;
      }
      this.#quickAdd.toggleAttribute('data-kent-pending', pending);
      this.#quickAdd.inert = pending || this.#quickAddWasInert;
    }
  }

  async #select(choice) {
    const request = ++this.#request;
    const productId = this.dataset.productId;
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#setPending(true);
    const status = this.querySelector('[data-colour-status]');
    const recovery = this.querySelector('[data-colour-recovery]');
    status.textContent = choice.dataset.loadingLabel;
    recovery.hidden = true;

    try {
      const requestUrl = new URL(choice.href, window.location.origin);
      requestUrl.searchParams.set('section_id', 'section-rendering-product-card');
      const currentUrl = new URL(window.location.href);
      for (const parameter of ['preview_theme_id', 'view']) {
        if (currentUrl.searchParams.has(parameter)) requestUrl.searchParams.set(parameter, currentUrl.searchParams.get(parameter));
      }
      const response = await fetch(requestUrl, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error('Colour section unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const variant = JSON.parse(html.querySelector('variant-picker script[type="application/json"]')?.textContent || 'null');
      const newPrice = html.querySelector('product-price [ref="priceContainer"]');
      const responseProductId = html.querySelector('product-card')?.dataset.productId;
      if (String(variant?.id) !== choice.dataset.variantId || responseProductId !== productId || !newPrice) {
        throw new Error('Colour section did not match');
      }
      const previewImage = new Image();
      previewImage.alt = choice.dataset.imageAlt;
      previewImage.className = 'kent-card-colour-preview';
      previewImage.src = choice.dataset.imageUrl;
      await previewImage.decode();
      if (request !== this.#request || !this.isConnected || this.dataset.productId !== productId) return;

      if (this.#card.matches('product-card')) {
        const gallery = this.#card.querySelector('[ref="cardGallery"]');
        if (!gallery) throw new Error('Colour image target unavailable');
        const oldPreview = gallery.querySelector('.kent-card-colour-preview');
        if (oldPreview) oldPreview.replaceWith(previewImage);
        else gallery.append(previewImage);
        for (const media of gallery.querySelectorAll('slideshow-component, .product-media-container')) {
          media.setAttribute('aria-hidden', 'true');
        }
      } else {
        const mainImage = this.#card.querySelector('[data-kent-colour-image]');
        if (!(mainImage instanceof HTMLImageElement)) throw new Error('Colour image target unavailable');
        mainImage.removeAttribute('srcset');
        mainImage.removeAttribute('sizes');
        mainImage.src = previewImage.src;
        mainImage.alt = previewImage.alt;
        mainImage.style.objectPosition = 'center';
        for (const secondary of this.#card.querySelectorAll('.resource-card__image--secondary')) {
          secondary.setAttribute('aria-hidden', 'true');
        }
      }
      this.#card.setAttribute('data-kent-colour-active', '');
      this.#card.setAttribute('data-product-transition', 'false');
      this.#card.setAttribute('data-featured-media-url', choice.dataset.imageUrl);

      for (const price of this.#card.querySelectorAll('product-price [ref="priceContainer"], [data-kent-colour-price]')) {
        price.replaceChildren(...[...newPrice.childNodes].map((node) => node.cloneNode(true)));
      }
      if (typeof this.#card.applyVariantToLinks === 'function') {
        this.#card.applyVariantToLinks(choice.dataset.variantId);
      } else {
        for (const link of this.#card.querySelectorAll('[ref="productCardLink"], [ref="cardGalleryLink"], [ref="productTitleLink"], [data-kent-colour-link]')) {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('variant', choice.dataset.variantId);
          link.href = url.href;
        }
      }
      for (const other of this.querySelectorAll('[role="button"][data-colour-name]')) {
        other.setAttribute('aria-pressed', String(other === choice));
      }
      const availability = variant.available ? '' : this.dataset.soldOutLabel;
      const selectedLabel = [choice.dataset.selectedLabel, availability].filter(Boolean).join(' — ');
      this.querySelector('[data-colour-selected]').textContent = selectedLabel;
      status.textContent = selectedLabel;
      if (this.#quickAdd?.querySelector('.quick-add__button--choose')) this.#quickAdd.dataset.quickAddButton = 'choose';
    } catch (error) {
      if (error.name === 'AbortError' || request !== this.#request || !this.isConnected) return;
      recovery.href = choice.href;
      recovery.textContent = choice.dataset.errorLabel;
      recovery.hidden = false;
      status.textContent = choice.dataset.errorLabel;
    } finally {
      if (request === this.#request) this.#setPending(false);
    }
  }
}

if (!customElements.get('kent-card-colours')) customElements.define('kent-card-colours', KentCardColours);
